import { cn } from '@/lib/cn';

/**
 * A list as cards on a phone, as a table on a laptop.
 *
 * ── WHY BOTH, RATHER THAN ONE RESPONSIVE THING ──
 * A table is the right shape for a laptop: seven columns, aligned, scannable
 * down a column. On a phone it is the wrong shape and no amount of CSS fixes
 * it. The invoice list showed Number, Customer and half a date; Status, Total
 * and Balance - the three figures the screen exists to show - sat off the right
 * edge behind a horizontal scroll that almost nobody discovers.
 *
 * Wrapping the table in a scroller was the fix for a laptop that is slightly
 * too narrow. It is not the fix for a phone, where the container is half the
 * width of the content and the important columns are the ones that fall off.
 *
 * So the page writes the row twice and `useIsPhone` picks one. That
 * duplication is deliberate and is the cheaper half of the trade - the
 * alternative is one markup that is a compromise at both sizes, and a
 * phone-first product cannot afford the phone to be the compromise.
 *
 * Picked in JavaScript rather than hidden in CSS because these rows contain
 * links and buttons: rendering both would put two of every control in the DOM,
 * announced twice by a screen reader and matched twice by every test selector.
 * See `useIsPhone`.
 */
export function Cards({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) {
  return <ul className={cn('divide-y divide-border', className)} {...props} />;
}

/**
 * One row.
 *
 * `min-h-16` because a row is a tap target: at four rows to a thumb-width it is
 * comfortable, and below that people hit the wrong invoice.
 */
export function CardRow({ className, ...props }: React.LiHTMLAttributes<HTMLLIElement>) {
  return <li className={cn('min-h-16 px-4 py-3', className)} {...props} />;
}

/**
 * The top line: what this row IS on the left, what it is WORTH on the right.
 *
 * Money goes here rather than in a labelled field below, because the figure is
 * the reason the row is being read. A phone layout that buries the amount under
 * three labels has reproduced the problem it set out to fix.
 */
export function CardLine({
  title,
  meta,
  value,
  sub,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  value?: React.ReactNode;
  sub?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{title}</div>
        {meta ? <div className="mt-0.5 truncate text-xs text-muted-foreground">{meta}</div> : null}
      </div>
      {value !== undefined ? (
        <div className="shrink-0 text-right">
          <div className="tabular font-medium">{value}</div>
          {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/** A row of small facts under the top line - status, dates, an account name. */
export function CardFacts({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}
